import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { Separator } from '@/components/ui/separator'
import { ExternalLink, CreditCard, Calendar, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { ExclamationTriangleIcon } from '@heroicons/react/16/solid'
import { useQueryClient } from '@tanstack/react-query'
import useGetBillingSubscription, {
  billingSubscriptionQueryKey,
} from '@/services/api/billing/useGetBillingSubscription'
import { useCreatePortalSession } from '@/services/api/billing/useBillingSessions'
import SpaceSelectComponent from './SpaceSelectComponent'
import useAuthStore from '@/store/useAuthStore'

export default function BillingSection() {
  const navigate = useNavigate()
  const currentTeamId = useAuthStore((state) => state.currentTeamId)
  const [selectedTeamId, setSelectedTeamId] = useState<string>(currentTeamId)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const queryClient = useQueryClient()

  const {
    data: subscription,
    isLoading: subscriptionLoading,
    status: subscriptionLoadingStatus,
    refetchForce,
  } = useGetBillingSubscription({ teamId: selectedTeamId })

  const portalSession = useCreatePortalSession()

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      queryClient.removeQueries({
        queryKey: billingSubscriptionQueryKey(selectedTeamId),
      })
      await refetchForce()
    } catch {
      toast.error('Failed to refresh subscription data')
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleManageSubscription = async () => {
    if (!selectedTeamId) {
      toast.error('Please select a space first')
      return
    }
    try {
      const { url } = await portalSession.mutateAsync(selectedTeamId)
      window.open(url, '_blank')
    } catch {
      toast.error('Failed to open billing portal')
    }
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })

  const sub = subscription?.subscription
  const isOpeningPortal = portalSession.isPending

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2>Billing</h2>
          <p className="text-muted-foreground">
            Manage your subscription and billing information
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing || subscriptionLoading || !selectedTeamId}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      <Separator />

      <div className="space-y-4">
        <SpaceSelectComponent
          selectedTeamId={selectedTeamId}
          onValueChange={(val) => setSelectedTeamId(val)}
        />

        {subscriptionLoadingStatus === 'error' && (
          <Card className="w-full p-6">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-destructive" />
              <span>Failed to load subscription details…</span>
            </div>
          </Card>
        )}

        {selectedTeamId && subscriptionLoadingStatus !== 'error' && (
          <div className="space-y-4">
            {subscriptionLoading ? (
              <Card className="w-full p-6">
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  <span>Loading subscription details…</span>
                </div>
              </Card>
            ) : subscription?.has_active_subscription ? (
              <Card className="w-full p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3>Current Subscription</h3>
                  <div
                    className={`rounded px-2 py-1 text-sm capitalize ${
                      sub?.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {sub?.status?.replace('_', ' ')}
                  </div>
                </div>

                <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Plan</p>
                      <p>{sub?.plan_name}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {sub?.is_trialing ? 'Trial ends' : 'Next billing'}
                      </p>
                      <p>
                        {sub?.is_trialing
                          ? sub.trial_ends_at
                            ? formatDate(sub.trial_ends_at)
                            : 'N/A'
                          : sub?.current_period_end
                            ? formatDate(sub.current_period_end)
                            : 'Not set'}
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleManageSubscription}
                  disabled={isOpeningPortal}
                  className="w-full"
                >
                  {isOpeningPortal ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Connecting to Stripe…
                    </>
                  ) : (
                    <>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Manage Subscription in Stripe
                    </>
                  )}
                </Button>
              </Card>
            ) : (
              <Card className="w-full p-6 text-center">
                <div className="mb-4">
                  <CreditCard className="mx-auto h-12 w-12 text-muted-foreground" />
                </div>
                <h3 className="mb-2">No Active Subscription</h3>
                <p className="mb-4 text-muted-foreground">
                  This space doesn&apos;t have an active subscription.
                </p>
                <div className="flex items-center justify-center gap-2">
                  <Button onClick={() => navigate('/billing')}>
                    View plans
                  </Button>
                  {/* Stripe's portal shows invoice history regardless of an
                      active subscription — this is the same portal session
                      as "Manage Subscription in Stripe" above, just reachable
                      even when there's nothing currently active to manage. */}
                  <Button
                    variant="outline"
                    onClick={handleManageSubscription}
                    disabled={isOpeningPortal}
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    View billing history
                  </Button>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
